<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterVersions',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    register: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_rowTemplate = computed(() => ({
    register_id: props.register.id,
  }));
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'name',
      header: $fn.value.pgettext('RegisterVersion', 'Name'),
      width: '110px',
      required: true,
    },
    {
      name: 'financial_year',
      width: '70px',
      header: $fn.value.pgettext('RegisterVersion', 'Financial year'),
      type: 'number',
      textAlign: 'right',
      required: true,
    },
    {
      name: 'number_format',
      width: '130px',
      header: $fn.value.pgettext('RegisterVersion', 'Number format'),
      type: 'textarea',
      required: true,
    },
    {
      name: 'current_value',
      width: '80px',
      header: $fn.value.pgettext('RegisterVersion', 'Current value'),
      type: 'number',
      textAlign: 'right',
      required: true,
    },
    {
      name: 'increment',
      width: '80px',
      header: $fn.value.pgettext('RegisterVersion', 'Increment'),
      type: 'number',
      textAlign: 'right',
      required: true,
    },
  ]);

  const $data = computed(() => ({
    ux: ux.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_rowTemplate: b_5962862420439144_rowTemplate.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_rowTemplate', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_columns', nodeId: '5962862420439144' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      ux,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_rowTemplate,
      b_5962862420439144_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.register,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('register');
    }
  );

  const act_5962862420439144_script_ayga2tulp_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('register_version').upsert($event.data.row)
    );
  };
  const act_5962862420439144_script_ayga2tulp_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_ayga2tulp_value_callable($event);
      },
    };
  };
  const act_5962862420439144_emit_xyrxrsnht_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5962862420439144_script_9r6wox0nh_value_callable = async (
    $event
  ) => {
    return $event.data.waitFor(
      $fn.value.crud('invoice_line_meter_accounting').delete($event.data.row)
    );
  };
  const act_5962862420439144_script_9r6wox0nh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_9r6wox0nh_value_callable($event);
      },
    };
  };
  const act_5962862420439144_emit_napqixub6_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };

  const act_7315092382398562_reloadComponentData_xos4ea58h =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_udzuem9wa =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_xos4ea58h,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_udzuem9wa],
    event: 'reload',
  });
  const act_5962862420439144_script_ayga2tulp = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_ayga2tulp_getActionArgs,
  });
  const inst_evh_5962862420439144_save_unhbdj0n6 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_ayga2tulp,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_save_unhbdj0n6],
    event: 'save',
  });
  const act_5962862420439144_emit_xyrxrsnht = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_xyrxrsnht_getActionArgs,
  });
  const inst_evh_5962862420439144_saveCompleted_qcawsqtj8 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_xyrxrsnht,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_saveCompleted_qcawsqtj8],
      event: 'save-completed',
    });
  const act_5962862420439144_script_9r6wox0nh = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_9r6wox0nh_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_1v6vo5qis =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_9r6wox0nh,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_1v6vo5qis],
    event: 'delete',
  });
  const act_5962862420439144_emit_napqixub6 = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_napqixub6_getActionArgs,
  });
  const inst_evh_5962862420439144_deleteCompleted_4zxswm3ld =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_napqixub6,
      event: 'delete-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_deleteCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_deleteCompleted_4zxswm3ld],
      event: 'delete-completed',
    });

  const actions = {
    inst_evh_7315092382398562_reload_udzuem9wa,
    inst_evh_7315092382398562_reload,
    inst_evh_5962862420439144_save_unhbdj0n6,
    inst_evh_5962862420439144_save,
    inst_evh_5962862420439144_saveCompleted_qcawsqtj8,
    inst_evh_5962862420439144_saveCompleted,
    inst_evh_5962862420439144_delete_1v6vo5qis,
    inst_evh_5962862420439144_delete,
    inst_evh_5962862420439144_deleteCompleted_4zxswm3ld,
    inst_evh_5962862420439144_deleteCompleted,
  };

  const evh_7315092382398562_reload_udzuem9wa = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_udzuem9wa.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save_unhbdj0n6 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save_unhbdj0n6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted_qcawsqtj8 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted_qcawsqtj8.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_1v6vo5qis = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_1v6vo5qis.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_deleteCompleted_4zxswm3ld = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_deleteCompleted_4zxswm3ld.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_deleteCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_deleteCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox alignItems="center" class="mb-2">
      <strong>
        {{ pgettext('Register', 'Versions') }}
      </strong>
    </ItpBox>
    <ItpDataTable
      rowKeyField="id"
      tableId="registerVersions"
      :paging="b_5962862420439144_paging"
      :editable="!register.isReadonly"
      :sortable="b_5962862420439144_sortable"
      :value="register.versions.data"
      :rowTemplate="b_5962862420439144_rowTemplate"
      :columns="b_5962862420439144_columns"
      v-if="register.versions.data"
      @save="evh_5962862420439144_save($event, {})"
      @save-completed="evh_5962862420439144_saveCompleted($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
      @delete-completed="evh_5962862420439144_deleteCompleted($event, {})"
    >
    </ItpDataTable>
  </ItpUsercontrol>
</template>
